import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { APP_VERSION } from "../constants/appVersion";

const utahData = "/img/UTDT_Logo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

const EngTable9 = ({ data }) => {
  const pdfRef = useRef();

  // Extract Values
  const labels = data.map((item) => item.agegrp_label);
  const female = data.map((item) => Number(item.total_fem_emp));
  const male = data.map((item) => Number(item.total_male_emp));

  // Analytics Summary
  const highestFemale = Math.max(...female);
  const highestFemaleGroup = labels[female.indexOf(highestFemale)];

  const highestMale = Math.max(...male);
  const highestMaleGroup = labels[male.indexOf(highestMale)];

  const chartData = {
    labels,
    datasets: [
      {
        label: "Female Employment Share (%)",
        data: female,
        backgroundColor: "#EC489960",
        borderColor: "#ec4899",
        borderWidth: 2,
      },
      {
        label: "Male Employment Share (%)",
        data: male,
        backgroundColor: "#0ea5e960",
        borderColor: "#0ea5e9",
        borderWidth: 2,
      },
    ],
  };

  const options = {
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "👩‍🦰 vs 👨 Employment Share by Age Group (2024)",
        font: { size: 18, weight: "bold" },
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        title: { display: true, text: "Employment Share (%)" },
        beginAtZero: true,
      },
      x: {
        title: { display: true, text: "Age Group" },
      },
    },
  };

  // ------- PDF Export -------
  const downloadPDF = () => {
    const input = pdfRef.current;
    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      const addHeader = () => {
        const logoWidth = 20; // ideal header width
        const logoHeight = 20; // adjust ratio if needed

        pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY,
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right",
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // Add more pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Final footer
      // ===== FINAL FOOTER SAFE AREA =====

      const footerTopY = pageHeight - 40; // start footer 40mm from bottom
      const footerContentY = pageHeight - 28; // logos row
      const footerBottomTextY = pageHeight - 10; // bottom text row

      // Top Divider Line
      pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

      // UtahData logo
      const utahWidth = 18;
      const utahHeight = 18;
      pdf.addImage(
        utahData,
        "PNG",
        20,
        footerContentY - 8,
        utahWidth,
        utahHeight,
      );

      // Built by
      pdf.setFontSize(10);
      pdf.text("Built by", 45, footerContentY);

      // BODEX logo
      const bodexWidth = 36;
      const bodexHeight = bodexWidth * 0.3;
      pdf.addImage(
        bodex,
        "PNG",
        60,
        footerContentY - 6,
        bodexWidth,
        bodexHeight,
      );

      // Using text
      pdf.text("using", 102, footerContentY);

      // KPI logo
      pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

      // Version
      pdf.text(APP_VERSION, 140, footerContentY);

      // Bottom Info Line
      pdf.setFontSize(8);
      pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
      pdf.text(
        `Downloaded on: ${new Date().toLocaleDateString()}`,
        pageWidth - 10,
        footerBottomTextY,
        null,
        null,
        "right",
      );

      pdf.save("employment_share_agegroup_2024.pdf");
    });
  };

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
          Employment Share by Age Group (2024)
        </h2>

        {/* Summary Boxes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 text-center">
          <div className="p-4 bg-pink-100 rounded-xl shadow">
            <p className="text-gray-600 text-sm font-medium">
              Highest Female Employment Share
            </p>
            <p className="text-2xl font-bold text-pink-700">
              {highestFemaleGroup} ({highestFemale}%)
            </p>
          </div>

          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-gray-600 text-sm font-medium">
              Highest Male Employment Share
            </p>
            <p className="text-2xl font-bold text-blue-700">
              {highestMaleGroup} ({highestMale}%)
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {" "}
          {/* Table */}
          <div className="overflow-x-auto mb-6">
            <table className="w-full text-center shadow-md border rounded-lg overflow-hidden">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Age Group</th>
                  <th className="p-2">Female (%)</th>
                  <th className="p-2">Male (%)</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row) => (
                  <tr key={row.agegrp_label} className="hover:bg-gray-100">
                    <td className="p-2">{row.agegrp_label}</td>
                    <td className="p-2 text-pink-600 font-semibold">
                      {row.total_fem_emp}
                    </td>
                    <td className="p-2 text-blue-600 font-semibold">
                      {row.total_male_emp}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* Export Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="Download" />
        </button>
      </div>
    </>
  );
};

export default EngTable9;

